import React from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { Card } from 'react-bootstrap-5';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Button } from 'components/ui/button';
import { listItemPropTypes, reorderableListItem } from 'helpers/react_dnd/reorderable_list';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faEllipsisVertical,
  faPenToSquare,
  faSparkles
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@buildoutinc/blueprint-react/ui/DropdownMenu';
import BlueprintTooltip from 'components/tooltip';
import MarkDigitallyAlteredModal from 'components/photos/mark_digitally_altered_modal';

export class PhotoRow extends React.Component {
  static propTypes = {
    ...listItemPropTypes,
    classSlug: PropTypes.string.isRequired,
    defaultPhotoOption: PropTypes.string.isRequired,
    digitallyAlteredEnabled: PropTypes.bool,
    isChecked: PropTypes.bool,
    photoAttachment: PropTypes.object.isRequired,
    photoEditorUrl: PropTypes.string,
    photoTypeOptions: PropTypes.array.isRequired,
    showEdit: PropTypes.bool,
    onAttributeChange: PropTypes.func.isRequired,
    onCheckboxClicked: PropTypes.func,
    onEdit: PropTypes.func.isRequired
  }

  static defaultPropTypes = {
    isChecked: false,
    showEdit: true
  }

  state = { showAlteredModal: false, isNarrow: false };

  cardRef = React.createRef();

  // Collapse the status badges to icon-only when the card is too narrow to fit all three labels on one line
  // (the full "Primary / Syndication Ready / Altered" row needs ~250px; desktop grid cards are ~297px and the
  // multi-column grid shrinks them to ~140px, so collapse below 280px).
  componentDidMount() {
    if (typeof ResizeObserver === 'undefined' || !this.cardRef.current) return;

    this.resizeObserver = new ResizeObserver(([entry]) => {
      this.setState({ isNarrow: entry.contentRect.width < 280 });
    });
    this.resizeObserver.observe(this.cardRef.current);
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }

  openAlteredModal = () => this.setState({ showAlteredModal: true });

  closeAlteredModal = () => this.setState({ showAlteredModal: false });

  handleDigitallyAlteredConfirm = (originalUrl) => {
    this.props.onAttributeChange(this.props.index, { originalUrl, digitallyAltered: true });
  };

  handleUnmarkAltered = () => {
    this.props.onAttributeChange(this.props.index, { digitallyAltered: false });
  };

  handleDescriptionUpdate = (e) => {
    this.props.onAttributeChange(this.props.index, { description: e.target.value });
  }

  handleAttachmentTypeUpdate = (e) => {
    this.props.onAttributeChange(this.props.index, { attachmentType: e.target.value });
  }

  handleSyndicateUpdate = (newValue) => {
    this.props.onAttributeChange(this.props.index, { syndicate: newValue });
  }

  deletePhoto = (e) => {
    e.preventDefault();
    this.props.onAttributeChange(this.props.index, { deleted: true });
  }

  getInputName = (key) => {
    let name = `${this.props.classSlug}[photo_attachments_attributes][${this.props.index}]`;
    const photoAttributeKeys = [
      'upload_id',
      '_destroy',
      'attachment_type',
      'id'
    ];

    if (photoAttributeKeys.indexOf(key) > -1) {
      name += `[photo_attributes]`;
    }
    name += `[${key}]`;
    return name;
  }

  render() {
    if (this.props.photoAttachment.deleted && this.props.photoAttachment.uploaded) {
      return null;
    }

    const { isChecked, photoAttachment, index } = this.props;
    const style = {
      opacity: this.props.isDragging ? 0 : 1,
      display: photoAttachment.deleted ? 'none' : 'block'
    };

    const photoRowBody = this.props.connectDragPreview(this.props.connectDropTarget(
      <div className="photo-row" key={index} name="photo-row-container" ref={this.cardRef} style={style}>
        <Card>
          <Card.Header className="position-relative p-0">
            {this.renderDragSource()}
            <div className="hover-overlay position-absolute top-0 start-0 w-100 h-100" />
            <div className={`${isChecked ? '' : 'hover-controls '}position-absolute top-0 start-0 p-2`}>
              <input
                checked={isChecked}
                className="form-check-input border-dark"
                style={{ width: '1.15rem', height: '1.15rem' }}
                type="checkbox"
                onChange={this.props.onCheckboxClicked}
              />
            </div>
            <div className="hover-controls position-absolute" style={{ right: '10px', top: '5.5px' }}>
              {this.renderActionsDropdown()}
            </div>
            <div
              className="position-absolute d-flex align-items-center gap-1"
              style={{ right: '10px', bottom: '8px' }}
            >
              {this.renderStatusBadge()}
            </div>
          </Card.Header>
          {this.renderDescriptionInput(photoAttachment, index)}
          <Card.Body className="p-2">
            <div className="d-flex align-items-center gap-2 justify-content-between">
              {this.props.showEdit && this.renderEditButton()}
              <div>
                { this.renderAttachmentTypeInput() }
              </div>
            </div>
            <div className="d-none">
              {this.renderHiddenIdField()}
              {this.renderHiddenSortOrderField()}
              {this.renderHiddenDeleteField()}
            </div>
          </Card.Body>
        </Card>
        <MarkDigitallyAlteredModal
          initialUrl={photoAttachment.originalUrl || ''}
          managing={photoAttachment.digitallyAltered}
          open={this.state.showAlteredModal}
          onClose={this.closeAlteredModal}
          onConfirm={this.handleDigitallyAlteredConfirm}
          onRemove={this.handleUnmarkAltered}
        />
      </div>
    ));

    return photoRowBody;
  }

  newSidekiqImage() {
    return !isEmpty(this.props.photoAttachment.sidekiqStatus);
  }

  bannerLabel() {
    if (this.newSidekiqImage()) {
      return this.props.photoAttachment.sidekiqStatus;
    } else {
      return 'Processing';
    }
  }

  photoPreviewStyle() {
    const { processing, sidekiqStatus: status } = this.props.photoAttachment;
    const url = this.props.photoAttachment.url;

    if (processing && (status === 'complete' || status === undefined)) {
      return { backgroundImage: `url(${url})` };
    } else if (processing) {
      return { backgroundImage: 'none' };
    } else {
      return { backgroundImage: `url(${url})` };
    }
  }

  renderTooltip() {
    const status = this.props.photoAttachment.sidekiqStatus;

    if (status === 'failed') {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          This media failed to process. This was mostly likely caused by an image that is too large.
          Try reducing the image size or contact Buildout support.
        </Tooltip>
      );
    } else if (status !== 'complete' && status != null) {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          Your media is being optimized for the best performance. This may take a few minutes.
        </Tooltip>
      );
    } else {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          Primary photos will be the first to be displayed in documents, email blasts and plugins.
          Choose a photo that best represents this {this.props.classSlug} as the primary photo.
        </Tooltip>
      );
    }
  }

  renderDragSource() {
    const labelStyle = {
      display: this.props.index === 0 ? 'block' : 'none'
    };

    return this.props.connectDragSource(
      <div className="js-photo-preview" style={this.photoPreviewStyle()} />
    );
  }

  renderStatusBadge() {
    const { processing, syndicate, digitallyAltered } = this.props.photoAttachment;
    const isPrimary = this.props.index === 0 && !this.props.isDragging;
    const { isNarrow } = this.state;

    if (processing) {
      return (
        <div className="photo-badge-container d-flex gap-1">
          <Badge appearance="muted" variant="secondary">{this.bannerLabel()}</Badge>
        </div>
      );
    }

    const extraBadges = [];
    if (syndicate) extraBadges.push({ label: 'Syndication Ready', icon: faCheck });
    if (digitallyAltered && this.props.digitallyAlteredEnabled) {
      extraBadges.push({ label: 'Altered', icon: faSparkles });
    }

    return (
      <div className="photo-badge-container d-flex gap-1">
        {isPrimary && (
          <OverlayTrigger overlay={this.renderTooltip()} placement="top">
            <Badge appearance="muted" className="text-primary" variant="secondary">Primary</Badge>
          </OverlayTrigger>
        )}
        {extraBadges.map((badge) => {
          const chip = (
            <Badge appearance="muted" variant="secondary">
              <FontAwesomeIcon className="text-primary" icon={badge.icon} />
              {!isNarrow && badge.label}
            </Badge>
          );

          // On a narrow card the labels won't all fit, so collapse each chip to just its icon and
          // surface the label through a tooltip instead.
          if (!isNarrow) return React.cloneElement(chip, { key: badge.label });

          return (
            <OverlayTrigger
              key={badge.label}
              overlay={(
                <Tooltip id={`${badge.label.replace(/\s+/g, '')}Tooltip${this.props.index}`}>
                  {badge.label}
                </Tooltip>
              )}
              placement="top"
            >
              <span>{chip}</span>
            </OverlayTrigger>
          );
        })}
      </div>
    );
  }

  renderHiddenIdField() {
    const { photoAttachment, index } = this.props;
    if (photoAttachment.uploaded) {
      return (
        <input
          name={this.getInputName('upload_id')}
          readOnly
          type="hidden"
          value={photoAttachment.uploadId}
        />
      );
    }
    let newUploadIdField = null;
    if (photoAttachment.uploadId) {
      newUploadIdField = (
        <input
          name={this.getInputName('upload_id')}
          readOnly
          type="hidden"
          value={photoAttachment.uploadId}
        />
      );
    }
    return (
      <>
        {newUploadIdField}
        <input
          name={`${this.props.classSlug}[photo_attachments_attributes][${index}][id]`}
          readOnly
          type="hidden"
          value={photoAttachment.id}
        />
        <input
          name={this.getInputName('photo_id')}
          readOnly
          type="hidden"
          value={photoAttachment.photoId}
        />
        <input
          name={this.getInputName('id')}
          readOnly
          type="hidden"
          value={photoAttachment.photoId}
        />
      </>
    );
  }

  renderHiddenSortOrderField() {
    return (
      <input
        name={this.getInputName('sort_order')}
        readOnly
        type="hidden"
        value={this.props.index}
      />
    );
  }

  renderHiddenDeleteField() {
    return (
      <input
        className="deletePhotoInput"
        name={this.getInputName('_destroy')}
        readOnly
        type="hidden"
        value={this.props.photoAttachment.deleted ? '1' : '0'}
      />
    );
  }

  renderDescriptionInput() {
    return (
      <input
        className="form-control border-0"
        name={this.getInputName('description')}
        placeholder="Add Caption"
        type="text"
        value={this.props.photoAttachment.description || ''}
        onChange={this.handleDescriptionUpdate}
      />
    );
  }

  displayedPhotoTypeOptions() {
    const { photoTypeOptions } = this.props;
    return photoTypeOptions.map(option => <option key={option} value={option}>{option}</option>);
  }

  renderAttachmentTypeInput() {
    const { defaultPhotoOption, photoAttachment, photoTypeOptions } = this.props;

    return (
      <select
        className="form-control form-select border-0 text-end"
        name={this.getInputName('attachment_type')}
        value={photoAttachment.attachmentType || defaultPhotoOption}
        onChange={this.handleAttachmentTypeUpdate}
      >
        {this.displayedPhotoTypeOptions()}
      </select>
    );
  }

  renderActionsDropdown() {
    const { syndicate, digitallyAltered } = this.props.photoAttachment;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger aria-label="Photo actions" className="photo-badge-button">
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => this.handleSyndicateUpdate(!syndicate)}>
            {syndicate ? 'Remove from Syndication' : 'Approve for Syndication'}
          </DropdownMenuItem>
          {this.props.digitallyAlteredEnabled && (
            digitallyAltered ? (
              <DropdownMenuItem onClick={this.openAlteredModal}>Manage digitally altered</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={this.openAlteredModal}>
                Mark digitally altered
                <BlueprintTooltip
                  container={document.body}
                  content="Adds a 'Digitally Altered' watermark to the photo for viewers"
                  dark
                  placement="right"
                >
                  <span className="ms-2"><FontAwesomeIcon icon={faCircleInfo} /></span>
                </BlueprintTooltip>
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  renderEditButton() {
    return (
      <Button
        icon={faPenToSquare}
        label="Edit"
        variant="ghost"
        onClick={() => this.props.onEdit(this.props.photoAttachment)}
      />
    );
  }
}

export default reorderableListItem(PhotoRow, 'photoForm', {
  beginDrag(props) {
    return {
      id: props.photoAttachment.id,
      index: props.index
    };
  }
});
